import { AxiosService } from 'src/axios/axios.service';
import { sentry } from 'src/sentry/sentry.singleton';
import { microsToDecimal, roundMicrosToCents } from '../Wallet.money';
import type {
  tLowBalanceDismissResponse,
  tSendCostEstimate,
  tSendCostEstimateResponse,
  tWalletBalance,
  tWalletReserved,
  tWalletLowBalance,
  tWalletBalanceResponse,
  tWalletCutoverReceipt,
  tWalletCutoverReceiptResponse,
  tFreeMonthDetail,
  tFreeMonthDetailResponse,
  tFreeTierCounter,
  tDayDetail,
  tDayDetailResponse,
  tWalletMovementsPage,
  tWalletMovementsResponse,
  tThresholdAmount,
  tThresholdCharge,
  tThresholdDisableResult,
  tThresholdOffReason,
  tThresholdPause,
  tThresholdRetryCharge,
  tThresholdRetryResult,
  tThresholdSaveResult,
  tThresholdTopUpState,
} from '../Wallet.types';
import {
  THRESHOLD_OFF_REASONS,
  THRESHOLD_PAUSE_REASONS,
  THRESHOLD_TOPUP_STATUSES,
  WALLET_CURRENCIES,
  WALLET_MESSAGE_CATEGORIES,
} from '../Wallet.types';
import type {
  tRecurringPresetsResponse,
  tRecurringTopUpResponse,
  tTopUpCharge,
  tTopUpChargeResponse,
  tTopUpDecline,
  tTopUpOptionsPayload,
  tWireActiveRecurring,
  tWireRecurring,
  tTopUpOptionsResponse,
} from '../components/TopUpModal/TopUpModal.types';
import type { tWalletPeriodicity } from '../Wallet.periodicity';

/**
 * WAPI wallet API surface.
 *
 * Every call goes through the BFF (`workspace/:wid/wallet/...`), never to
 * wallet-service directly, and that is not a style choice: wallet-service
 * requires an internal key on its workspace-scoped routes, and that key cannot
 * live in a browser. The BFF holds it and is the layer that knows who the user
 * is. See src/wallet in bff-web.
 *
 * The whole surface sits behind `billing_wapi-wallet`, off everywhere.
 *
 * ⚠️ Paths must match the BFF exactly, and a typo does NOT surface as a 404.
 * That BFF registers its auth middleware globally, before route matching, so an
 * unregistered path answers 401 — indistinguishable from an expired session.
 * This file used to call `wallet/{ws}/top-up-options`, which never existed;
 * it read as a login problem. Every path here is `workspace/{wid}/wallet/...`.
 */

/**
 * A declined card (402). Carries the structured reason so the UI can say what
 * to do next instead of "something went wrong" — a generic message is what
 * pushes a customer to retry the same card, which hardens the decline.
 */
export class WalletDeclinedError extends Error {
  readonly decline: tTopUpDecline;

  constructor(decline: tTopUpDecline) {
    super(`wallet top-up declined: ${decline.reason}`);
    this.name = 'WalletDeclinedError';
    this.decline = decline;
  }
}

/** Thrown when the payload does not match the money contract. */
export class WalletContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WalletContractError';
  }
}

/**
 * Un 400 o un 409 que wallet-service (o el BFF) contestó sobre ESTA petición,
 * en las rutas de la recarga por saldo bajo (RET-2425).
 *
 * Expone solo lo que el front puede traducir: `reason` (409, y el 400
 * `payment_method_selection_unavailable` del retry), `code` y `limitMicros`
 * (400 `invalid_amount`). El campo `error` del cuerpo NO se copia aquí a
 * propósito: es texto para logs, nunca para el cliente (R17). Un 409 sin
 * `reason` (varios clientes de pago, moneda) o un 400 sin `code` llegan con los
 * dos en null, y la pantalla usa el texto genérico.
 *
 * `limitMicros` es null cuando wallet manda "" (la cota no tiene cifra) o un
 * valor que no es un entero exacto.
 */
export class WalletRejectedError extends Error {
  readonly status: number;
  readonly reason: string | null;
  readonly code: string | null;
  readonly limitMicros: string | null;

  constructor(rejection: {
    status: number;
    reason: string | null;
    code: string | null;
    limitMicros: string | null;
  }) {
    super(
      `wallet threshold top-up rejected: ${rejection.status} ${
        rejection.reason ?? rejection.code ?? 'without reason'
      }`,
    );
    this.name = 'WalletRejectedError';
    this.status = rejection.status;
    this.reason = rejection.reason;
    this.code = rejection.code;
    this.limitMicros = rejection.limitMicros;
  }
}

/**
 * El 404 de `GET …/topup/threshold` que SÍ escribió wallet-service: el
 * workspace no tiene plan vivo o no tiene cliente de pago, así que no hay
 * moneda con qué pintar los límites. Equivale al 409 `no_wapi` de las
 * escrituras (RC-15). La opción de saldo bajo se oculta sin registrar nada,
 * porque es una respuesta de negocio, no una falla (KTD5).
 */
export class WalletThresholdUnavailableError extends Error {
  readonly reason: 'no_plan_subscription' | 'no_payment_customer';
  /**
   * El `offReason` del cuerpo del 404 (RET-2423): el sistema apagó la recarga y
   * ya no hay plan vivo, y dice por qué. Null cuando el 404 no lo trae (sin
   * configuración y sin plan) o trae un valor fuera de la lista.
   */
  readonly offReason: tThresholdOffReason | null;

  constructor(
    reason: 'no_plan_subscription' | 'no_payment_customer',
    offReason: tThresholdOffReason | null = null,
  ) {
    super(`wallet threshold top-up unavailable: ${reason}`);
    this.name = 'WalletThresholdUnavailableError';
    this.reason = reason;
    this.offReason = offReason;
  }
}

/**
 * Un 404 que NO escribió wallet-service: la ruta no existe en algún salto
 * (`{"message":"404 page not found\n"}` del router de wallet relayado por el
 * BFF, el sobre del BFF sin `error`, o el `Cannot GET …` de Fiber). La opción
 * se oculta igual, pero esto sí es una falla de despliegue y se reporta a
 * Sentry desde el servicio (KTD5).
 */
export class WalletRouteMissingError extends Error {
  constructor(endpoint: string) {
    super(`wallet route missing: ${endpoint}`);
    this.name = 'WalletRouteMissingError';
  }
}

/**
 * Validates the money payload at the network boundary (review finding #3).
 *
 * Without this, `setBalance(response.data)` trusts whatever arrives: a 200
 * carrying `success: false`, or the flat (unwrapped) envelope this BFF is
 * documented to return in some deployments (see FeatureFlags.provider.tsx),
 * would store `undefined` in a field typed `tWalletBalance | null` — breaking
 * every `=== null` guard downstream and making a backend failure
 * indistinguishable from "this workspace has no wallet".
 *
 * Amounts must be STRINGS. A number here is not a cosmetic difference: JSON
 * numbers go through a float, and micro-units past 2^53 lose precision — real
 * money, silently. Reject loudly instead.
 */
function parseWalletBalance(payload: unknown): tWalletBalance {
  const envelope = payload as Partial<tWalletBalanceResponse> | undefined;

  if (!envelope || typeof envelope !== 'object') {
    throw new WalletContractError('wallet balance: empty response');
  }
  if (envelope.success !== true) {
    throw new WalletContractError('wallet balance: success flag not true');
  }

  const data = envelope.data as Partial<tWalletBalance> | undefined;
  if (!data || typeof data !== 'object') {
    throw new WalletContractError('wallet balance: missing data');
  }
  if (
    typeof data.amount !== 'string' ||
    typeof data.amountMicros !== 'string'
  ) {
    throw new WalletContractError(
      'wallet balance: amount and amountMicros must be strings',
    );
  }
  // Empty is a VALID currency here, and only in one case: the wallet derives it
  // from its own ledger, so a wallet that has never moved money has none to
  // report (RET-1685, confirmed in RET-1684). Rejecting it would turn the
  // normal state of a brand-new workspace into a read error, which the card
  // paints as "we could not read your balance".
  if (
    data.currency !== '' &&
    !WALLET_CURRENCIES.includes(data.currency as never)
  ) {
    throw new WalletContractError(
      `wallet balance: unsupported currency ${String(data.currency)}`,
    );
  }
  // Ausente es válido: el umbral de saldo bajo (RET-1760) todavía no existe en
  // el backend, así que el payload de hoy no lo trae. Presente pero malformado
  // sí se rechaza — eso sería una regresión del contrato, no una fase.
  if (data.lowBalance && typeof data.lowBalance.triggered !== 'boolean') {
    throw new WalletContractError('wallet balance: malformed lowBalance');
  }

  // `reserved` solo explica el saldo (RET-2299): uno mal formado se descarta y
  // el saldo se sirve igual. Se copia en vez de borrar la clave para no mutar
  // el payload que entregó axios.
  const { reserved, ...balance } = data;

  return {
    ...(balance as tWalletBalance),
    ...(isWalletReserved(reserved) ? { reserved } : {}),
  };
}

/**
 * Dos strings y un monto que se pueda mostrar, o nada. La guarda de tipo
 * importa: `hasPositiveMicros` acepta un número porque `BigInt(30250000)`
 * funciona, y un número en micros ya perdió precisión al pasar por JSON.
 *
 * El monto se valida AQUÍ, en la frontera, porque el contrato de este parser es
 * que un `reserved` malformado se descarta y el saldo se sirve. Si pasara
 * entero, `formatWalletAmount` lanzaría `RangeError` al pintarlo y tumbaría la
 * tarjeta de saldo completa por un campo opcional.
 *
 * - `/^\d+$/` va primero: `BigInt` acepta `' 5'` y `'0x10'`, y lanza con basura.
 * - La cota es la misma que lanza `formatWalletAmount`: centavos por encima de
 *   `Number.MAX_SAFE_INTEGER`.
 */
function isWalletReserved(value: unknown): value is tWalletReserved {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<tWalletReserved>;

  if (
    typeof candidate.amount !== 'string' ||
    typeof candidate.amountMicros !== 'string'
  ) {
    return false;
  }

  if (!/^\d+$/.test(candidate.amountMicros)) return false;

  return (
    roundMicrosToCents(candidate.amountMicros) <=
    BigInt(Number.MAX_SAFE_INTEGER)
  );
}

/**
 * Deterministic idempotency key for a top-up charge (RET-1852).
 *
 * MUST be a pure function of the three inputs: same (workspaceId,
 * amountMicros, attemptId) -> same key, every time. That determinism is the
 * fix for the bug this ticket reports — if Stripe charges but the response is
 * lost on the way back, the modal stays open, the customer retries, and the
 * retry must produce the SAME key so Stripe dedupes instead of charging a
 * second time on a balance the SSOT (P16 / RET-1727) will not refund.
 *
 * Each input pulls its own weight, and dropping any one reopens a real hole:
 *   - `attemptId` changing must change the key. This is the trap the naive
 *     "derive from (workspace, amount)" fix falls into: Stripe idempotency
 *     keys live 24h, so a customer who deliberately tops up the same amount
 *     twice in one day would have the SECOND charge silently swallowed —
 *     Stripe returns the first PaymentIntent, nothing is charged, nothing is
 *     credited, and nobody reports it because the UI looks like success.
 *     `attemptId` is what tells the two charges apart: it identifies the
 *     customer's INTENT to charge, not the request wire format.
 *   - `amountMicros` changing must change the key even within the SAME
 *     attemptId (RET-1846): wallet-service 400s
 *     (`domain.ErrIdempotencyKeyReused`) on a key reused with different
 *     parameters, and that 400 reads as gibberish to someone who only edited
 *     the amount after a failed try.
 *   - `workspaceId` changing must change the key, defensively — two
 *     workspaces must never be able to collide on the same Stripe key.
 *
 * Each part goes through `encodeURIComponent` before joining with `:` so none
 * of the three can inject that delimiter and blur the boundary between
 * fields (an attemptId containing a literal ":" must not be able to make
 * `ws:1` + `500` collide with `ws` + `1:500`).
 */
/**
 * Un cupo «no disponible» SIN razón no es un desenlace: es un campo que no llegó.
 *
 * ── Por qué hace falta una regla tan fina ──
 *
 * El contador del cupo se perdió entre wallet-service y el BFF durante tres semanas
 * (RET-2235) y NADIE se enteró, porque el payload que produce esa pérdida es
 * **idéntico** al de una negativa legítima: `{ available: false, total: null }`. La
 * estructura no los separa. El panel mostró su aviso honesto —«no pudimos calcular tu
 * cupo»— encima de un contrato roto, con el copy bien cuidado, todos los días.
 *
 * Lo que SÍ los separa es la razón. El servicio de WhatsApp lo declara en su propio
 * contrato: `UnavailableReason` es una de sus constantes y **está vacía sólo si el
 * cupo está disponible**. Los tres caminos por los que se niega a afirmarlo —tabla de
 * reglas ilegible, sin reglas, combinación sin clasificar— ponen los tres su razón.
 *
 * Entonces «no disponible y sin razón» es un estado que **nadie río arriba puede
 * producir**, y por eso vale como firma de que el dato se perdió en el camino.
 *
 * ── Por qué NO lanza ──
 *
 * Porque el cliente no tiene la culpa ni el arreglo. Lanzar cambiaría el panel al
 * estado de error y le quitaría el total del mes, que es correcto y es lo único que
 * tiene. Un contrato roto se le cuenta a quien puede arreglarlo, no a quien lo sufre.
 *
 * ── Por qué se condiciona a que la mitad gratis SÍ haya cargado ──
 *
 * Si no cargó, que el contador venga vacío es la consecuencia esperada y ya está
 * dicha por su propia bandera. Reportarlo ahí sería ruido — y el ruido en Sentry es
 * cómo se aprende a ignorar Sentry.
 *
 * ── `cap` (RET-2283, arreglo B del orquestador tras la vuelta 1) ──
 *
 * `cap` se agregó en OTRO salto que `total`/`available` — lo tuvo que sumar la
 * rebanada de bff-web, por separado — así que puede perderse SOLO, sin tocar el
 * resto del payload. Si eso pasa, la firma es `available: true, total: N, cap`
 * AUSENTE: el sello (`useComposerServiceSeal`, que lee `cap == null` como "no
 * sé") se calla para siempre, y este vigilante — que hasta ahora sólo miraba
 * `available` y `unavailableReason` — no se enteraba, exactamente como no se
 * enteró de `total` durante las tres semanas de RET-2235.
 *
 * `cap: null` es un VALOR legítimo ("sin tope"); `cap` AUSENTE (`undefined`,
 * la clave ni llegó) no lo es — la vista lo manda SIN `omitempty`, así que la
 * clave debe existir siempre. Por eso se comprueba con `=== undefined`, no con
 * `== null`: lo segundo confundiría el valor legítimo con el campo perdido.
 *
 * ── Por qué reporta sólo UNA vez por workspace y sesión (arreglo C) ──
 *
 * Esta función corre en cada llamada a `getFreeMonthDetail`/`getDayDetail`, y
 * desde RET-2283 `WalletProvider` llama a la primera cada 60 s mientras haya un
 * chat WAPI abierto. Reportar cada corrida convertiría "una vez al abrir el tab
 * Saldo" en "una por minuto por pestaña" — multiplicado por la cohorte (879
 * workspaces hoy) es un flood, y el ruido en Sentry es cómo se aprende a
 * ignorarlo, que es justo el argumento de este mismo docblock para el campo
 * perdido. `reportedTierCounterIssues` recuerda qué combinación (razón,
 * endpoint, workspace) ya se avisó en esta sesión del módulo y calla el resto
 * — literalmente "una vez por workspace y sesión", sin reabrirse si el
 * problema se repite más tarde EN LA MISMA sesión: ya se sabe de él, y
 * re-avisar cada 60 s no le agrega información a quien lo va a arreglar.
 */
const reportedTierCounterIssues = new Set<string>();

/**
 * SOLO para tests (RET-2283, vuelta 2). El Set de arriba vive a nivel de
 * módulo a propósito — el reporte debe sobrevivir entre llamadas DENTRO de la
 * misma sesión de la pestaña, que es justo "una vez por workspace y sesión".
 * Pero esa misma vida larga se cuela en los tests: vitest aísla el registro
 * de módulos POR ARCHIVO (ver `.claude/rules/testing.md` — nunca `isolate:
 * false`), así que el Set sobrevive entre los `it()` de un mismo archivo. Un
 * test anterior que reporta por una clave deja esa clave armada para el
 * resto; si un test posterior rompe el guardia y DEBERÍA reportar por la
 * misma clave, el dedupe lo calla y el negativo pasa por la razón
 * equivocada, no porque el guardia siga sano. Los archivos de prueba de este
 * módulo deben llamar esto en su `beforeEach`, junto a `vi.clearAllMocks()`.
 */
export function resetTierCounterIssueReportsForTests(): void {
  reportedTierCounterIssues.clear();
}

function reportTierCounterIssueOnce(
  reason: string,
  context: { endpoint: string; workspaceId: string; period: string },
): void {
  const { endpoint, workspaceId, period } = context;
  const dedupeKey = `${reason}::${endpoint}::${workspaceId}`;

  if (reportedTierCounterIssues.has(dedupeKey)) return;
  reportedTierCounterIssues.add(dedupeKey);

  sentry.captureException(new WalletContractError(reason), {
    extra: { endpoint, workspaceId, period },
  });
}

function reportUnexplainedTierCounter(options: {
  counter: tFreeTierCounter | undefined;
  freeHalfLoaded: boolean;
  endpoint: string;
  workspaceId: string;
  period: string;
}): void {
  const { counter, freeHalfLoaded, endpoint, workspaceId, period } = options;

  if (!freeHalfLoaded) return;

  if (counter?.available) {
    // Arreglo B: un cupo que SÍ se afirma pero sin `cap` es el mismo defecto
    // que `total` ausente, con la misma firma de "se perdió en el camino".
    if (counter.cap === undefined) {
      reportTierCounterIssueOnce('wallet: tier counter available without cap', {
        endpoint,
        workspaceId,
        period,
      });
    }

    return;
  }

  if (counter?.unavailableReason) return;

  reportTierCounterIssueOnce(
    counter
      ? 'wallet: tier counter unavailable with no reason'
      : 'wallet: tier counter missing from the payload',
    { endpoint, workspaceId, period },
  );
}

/**
 * Un `amountMicros` legible: entero decimal, con signo opcional, como STRING.
 *
 * Se valida con una expresión y NO metiendo `BigInt(value)` en un try/catch, y la
 * diferencia es justo el caso que importa: `BigInt("")` devuelve `0n` **sin lanzar**,
 * así que un campo que no llegó pasaría el try como un cero perfectamente válido —
 * que es exactamente cómo el desfase de nombres de RET-2235 sobrevivió sin ruido.
 *
 * Tampoco acepta decimales ni notación científica: el contrato dice entero exacto en
 * micros precisamente para que nadie tenga que redondear dos veces.
 */
function isReadableMicros(value: unknown): boolean {
  return typeof value === 'string' && /^-?\d+$/.test(value);
}

function idempotencyKeyFor(attempt: {
  workspaceId: string;
  amountMicros: string;
  attemptId: string;
}): string {
  const { workspaceId, amountMicros, attemptId } = attempt;

  // Falla RUIDOSO en vez de componer una llave válida-pero-colisionable. Un
  // attemptId vacío produce `topup:<ws>:<monto>:`, que pasa el `required` del BFF
  // y el de wallet-service —los dos solo comprueban no-vacío— y colisiona con
  // CUALQUIER otro cobro del mismo workspace y monto dentro de las 24h que vive
  // la llave en Stripe. Eso no da un doble cobro: da lo contrario y peor, un
  // no-cobro SILENCIOSO (Stripe devuelve el intent viejo, el front lo lee como
  // éxito, el saldo nunca se mueve y nadie lo reporta).
  if (!workspaceId || !amountMicros || !attemptId) {
    throw new WalletContractError(
      'wallet top-up: idempotency key needs workspace, amount and attempt',
    );
  }

  // RET-2428: la tarjeta NO entra en la llave. Queda fijada al intento
  // (`readOrCreateOneOffAttempt`), así que un reintento tras un desenlace
  // desconocido repite la misma llave con la misma tarjeta y wallet-service
  // repite el cobro en vez de cobrar otra vez.
  return [
    'topup',
    encodeURIComponent(workspaceId),
    encodeURIComponent(amountMicros),
    encodeURIComponent(attemptId),
  ].join(':');
}

/**
 * Extracts the structured decline from a 402, or null when the error is
 * anything else.
 *
 * Only a 402 is treated as a decline. A 424 (wallet-service unreachable) or a
 * 403 (not an owner/admin) are different problems with different fixes, and
 * dressing them up as "your card was declined" sends the customer to their bank
 * over an outage.
 */
function declineFrom(error: unknown): tTopUpDecline | null {
  const response = (error as { response?: { status?: number; data?: unknown } })
    ?.response;

  if (response?.status !== 402) return null;

  const decline = (response.data as { data?: tTopUpDecline })?.data;

  if (!decline?.reason) {
    // A 402 whose body we cannot read is still a decline: saying "service
    // unavailable" would send the customer to retry blindly. Unknown reason,
    // and not retryable with the same card, because we cannot justify a retry.
    return {
      error: 'payment_declined',
      declineCode: '',
      reason: 'unknown',
      retryableWithSameCard: false,
    };
  }

  return decline;
}

// ── Recarga por saldo bajo (RET-2425): frontera del contrato ─────────────────

/**
 * Los dos textos con que wallet-service contesta 404 en el GET
 * (`threshold_topup_controller.go`, `Get`). Son los ÚNICOS 404 que significan
 * «wallet dice que no» (nota del BFF, RET-2424): cualquier otro 404, aunque
 * traiga `error`, se trata como ruta ausente y se reporta.
 */
const THRESHOLD_UNAVAILABLE_404: Record<
  string,
  'no_plan_subscription' | 'no_payment_customer'
> = {
  'no active plan subscription for workspace': 'no_plan_subscription',
  'no payment customer for workspace': 'no_payment_customer',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function responseOf(error: unknown): { status: number; data: unknown } | null {
  if (!isRecord(error) || !isRecord(error.response)) return null;

  const { status, data } = error.response;

  return typeof status === 'number' ? { status, data } : null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * El `offReason` de contrato, o null. Un valor fuera de la lista no lanza: la
 * lectura sigue y la recarga se pinta apagada sin motivo (el texto de venta),
 * que es lo que se mostraría si la hubiera apagado una persona. Se prefirió a
 * un miembro `unknown` porque el motivo no tendría texto que mostrar.
 */
function parseOffReason(value: unknown): tThresholdOffReason | null {
  return typeof value === 'string' &&
    (THRESHOLD_OFF_REASONS as readonly string[]).includes(value)
    ? (value as tThresholdOffReason)
    : null;
}

/**
 * Tipa un 400 o un 409 de las rutas de saldo bajo. Devuelve null para
 * cualquier otro status. El cuerpo del rechazo llega SUELTO: el BFF relaya el
 * JSON de wallet tal cual (`upstreamError.Body`), sin el sobre
 * `{success, data}`. Un 400 de validación del propio BFF trae el sobre y no
 * trae `reason` ni `code`, así que cae en el genérico.
 */
function rejectionFrom(error: unknown): WalletRejectedError | null {
  const response = responseOf(error);

  if (!response || (response.status !== 400 && response.status !== 409)) {
    return null;
  }

  const body = isRecord(response.data) ? response.data : {};
  const limit = body.limitMicros;

  return new WalletRejectedError({
    status: response.status,
    reason: nonEmptyString(body.reason),
    code: nonEmptyString(body.code),
    limitMicros:
      typeof limit === 'string' && /^\d+$/.test(limit) ? limit : null,
  });
}

/** Si un 404 del GET lo escribió wallet (Unavailable) o es una ruta ausente. */
function notFoundFrom(
  error: unknown,
  endpoint: string,
): WalletThresholdUnavailableError | WalletRouteMissingError | null {
  const response = responseOf(error);

  if (response?.status !== 404) return null;

  const walletError = isRecord(response.data) ? response.data.error : undefined;
  const reason =
    typeof walletError === 'string'
      ? THRESHOLD_UNAVAILABLE_404[walletError]
      : undefined;

  // El BFF relaya el 404 de wallet SUELTO, sin sobre: `offReason` va en la
  // raíz del cuerpo, junto a `error` (`thresholdOffNotFoundJSON`).
  const offReason = isRecord(response.data)
    ? parseOffReason(response.data.offReason)
    : null;

  return reason
    ? new WalletThresholdUnavailableError(reason, offReason)
    : new WalletRouteMissingError(endpoint);
}

function isThresholdAmount(value: unknown): value is tThresholdAmount {
  return (
    isRecord(value) &&
    typeof value.amount === 'string' &&
    typeof value.amountMicros === 'string' &&
    /^\d+$/.test(value.amountMicros)
  );
}

function isNullableThresholdAmount(value: unknown): boolean {
  return value === null || isThresholdAmount(value);
}

function isNullableString(value: unknown): boolean {
  return value === null || typeof value === 'string';
}

/**
 * Valida y tipa el `pause` del estado (`thresholdPauseJSON`, RET-2421). Null
 * pasa como null. Cada campo del contrato tiene que LLEGAR (van sin omitempty
 * en Go), por la misma razón que el resto del estado.
 *
 * Un `reason` fuera de la lista NO lanza: la pantalla tiene que poder pintar
 * una pausa que este front no conoce. Se trata como un rechazo del banco sin
 * motivo conocido: `reason: declined`, `declineReason: unknown` y
 * `retryableWithSameCard: false`. Forzar el false importa: ofrecer «Reintentar
 * el cobro» sobre una pausa que no se entiende repetiría un cobro que el banco
 * puede estar rechazando por una razón que nadie leyó.
 */
function parsePause(
  value: unknown,
  fail: (field: string) => never,
): tThresholdPause | null {
  if (value === null) return null;
  if (!isRecord(value)) return fail('pause');

  const {
    reason,
    since,
    until,
    charge,
    declineReason,
    retryableWithSameCard,
    card,
    oneOffResumes,
  } = value;

  if (typeof reason !== 'string') fail('pause.reason');
  if (typeof since !== 'string') fail('pause.since');
  if (!('until' in value) || !isNullableString(until)) fail('pause.until');
  if (
    !('charge' in value) ||
    (charge !== null && !(isRecord(charge) && isThresholdAmount(charge.amount)))
  ) {
    fail('pause.charge');
  }
  if (!('declineReason' in value) || !isNullableString(declineReason)) {
    fail('pause.declineReason');
  }
  if (typeof retryableWithSameCard !== 'boolean') {
    fail('pause.retryableWithSameCard');
  }
  if (
    !('card' in value) ||
    (card !== null &&
      !(
        isRecord(card) &&
        typeof card.brand === 'string' &&
        typeof card.last4 === 'string'
      ))
  ) {
    fail('pause.card');
  }
  if (
    !('oneOffResumes' in value) ||
    (oneOffResumes !== null && typeof oneOffResumes !== 'boolean')
  ) {
    fail('pause.oneOffResumes');
  }

  const known = (THRESHOLD_PAUSE_REASONS as readonly string[]).includes(
    reason as string,
  );

  return {
    reason: known ? (reason as tThresholdPause['reason']) : 'declined',
    since: since as string,
    until: until as string | null,
    charge:
      charge === null
        ? null
        : { amount: (charge as { amount: tThresholdAmount }).amount },
    declineReason: known ? (declineReason as string | null) : 'unknown',
    retryableWithSameCard: known ? (retryableWithSameCard as boolean) : false,
    card:
      card === null
        ? null
        : {
            brand: (card as { brand: string }).brand,
            last4: (card as { last4: string }).last4,
          },
    oneOffResumes: oneOffResumes === true,
  };
}

/**
 * Valida el `state` en la frontera. Cada campo del contrato tiene que LLEGAR:
 * del lado de Go van sin omitempty, así que un campo ausente es un contrato
 * roto, no un null. El caso que esto evita es el de RET-2050 y RET-2235: un
 * campo perdido en el camino que el front lee como su valor cero y pinta como
 * dato real (aquí, un `chargeInProgress` perdido prometería un cobro que ya
 * está en curso).
 *
 * `pause` se valida con `parsePause` y `offReason` con `parseOffReason`: los
 * dos devuelven el valor ya tipado, y un valor desconocido no lanza (ver cada
 * uno). El resto del objeto pasa tal cual, para no descartar campos.
 */
function parseThresholdState(
  value: unknown,
  where: string,
): tThresholdTopUpState {
  const fail = (field: string): never => {
    throw new WalletContractError(`${where}: malformed state (${field})`);
  };

  if (!isRecord(value)) return fail('state');

  if (
    typeof value.status !== 'string' ||
    !(THRESHOLD_TOPUP_STATUSES as readonly string[]).includes(value.status)
  ) {
    fail('status');
  }
  if (
    typeof value.currency !== 'string' ||
    !(WALLET_CURRENCIES as readonly string[]).includes(value.currency)
  ) {
    fail('currency');
  }
  if (!isNullableThresholdAmount(value.threshold)) fail('threshold');
  if (!isNullableThresholdAmount(value.amount)) fail('amount');

  const { lastValues, limits, today, chargeInProgress, typeChange } = value;

  if (
    lastValues !== null &&
    !(
      isRecord(lastValues) &&
      isThresholdAmount(lastValues.threshold) &&
      isThresholdAmount(lastValues.amount)
    )
  ) {
    fail('lastValues');
  }
  if (
    !isRecord(limits) ||
    !isThresholdAmount(limits.thresholdMin) ||
    !isThresholdAmount(limits.thresholdDefault) ||
    !isThresholdAmount(limits.amountMin) ||
    !isThresholdAmount(limits.amountMax) ||
    !isThresholdAmount(limits.amountDefault) ||
    typeof limits.dailyMax !== 'number'
  ) {
    fail('limits');
  }
  if (!isRecord(today) || typeof today.count !== 'number') fail('today');
  if (!('pause' in value)) fail('pause');

  const pause = parsePause(value.pause, fail);

  if (
    chargeInProgress !== null &&
    !(
      isRecord(chargeInProgress) &&
      isThresholdAmount(chargeInProgress.amount) &&
      typeof chargeInProgress.startedAt === 'string'
    )
  ) {
    fail('chargeInProgress');
  }
  if (!isNullableString(value.offReason)) fail('offReason');
  if (!isNullableString(value.endsOn)) fail('endsOn');
  if (
    !isRecord(typeChange) ||
    typeof typeChange.allowed !== 'boolean' ||
    !isNullableString(typeChange.blockedBy) ||
    !isNullableString(typeChange.scheduledChangeOn)
  ) {
    fail('typeChange');
  }

  return {
    ...value,
    pause,
    offReason: parseOffReason(value.offReason),
  } as tThresholdTopUpState;
}

/**
 * El sobre del BFF en un 200 de las rutas de saldo bajo: `{success, data}`,
 * con `data` igual al JSON de wallet sin re-tipar (`thresholdTopUpResponse`).
 */
function thresholdDataOf(
  response: unknown,
  where: string,
): Record<string, unknown> {
  if (!isRecord(response) || response.success !== true) {
    throw new WalletContractError(`${where}: success flag not true`);
  }
  if (!isRecord(response.data)) {
    throw new WalletContractError(`${where}: missing data`);
  }

  return response.data;
}

function parseThresholdCharge(
  value: unknown,
  where: string,
): tThresholdCharge | null {
  if (value === null) return null;
  if (
    isRecord(value) &&
    typeof value.status === 'string' &&
    isThresholdAmount(value.amount)
  ) {
    return { status: value.status, amount: value.amount };
  }

  throw new WalletContractError(`${where}: malformed charge`);
}

function parseThresholdSaveResult(
  response: unknown,
  where: string,
): tThresholdSaveResult {
  const data = thresholdDataOf(response, where);

  // `charge` tiene que llegar, aunque sea null: va sin omitempty en Go.
  if (!('charge' in data)) {
    throw new WalletContractError(`${where}: missing charge`);
  }

  return {
    state: parseThresholdState(data.state, where),
    charge: parseThresholdCharge(data.charge, where),
  };
}

/**
 * El 200 del retry (`thresholdRetryResponse`): `{state, charge}` con `charge`
 * siempre presente y con `clientSecret`.
 *
 * `clientSecret` solo sobrevive con `status: requires_action`; con cualquier
 * otro status queda en null aunque el cuerpo lo traiga, para que un secreto que
 * nadie va a usar no circule. Ausente se lee como null: el efecto es no ofrecer
 * la autenticación del banco, que es el lado seguro.
 *
 * Los mensajes de error nombran el campo y nunca el valor: el secreto no puede
 * llegar a Sentry ni a ningún log por esta vía.
 */
function parseThresholdRetryResult(
  response: unknown,
  where: string,
): tThresholdRetryResult {
  const data = thresholdDataOf(response, where);
  const raw = data.charge;

  if (
    !isRecord(raw) ||
    typeof raw.status !== 'string' ||
    !isThresholdAmount(raw.amount)
  ) {
    throw new WalletContractError(`${where}: malformed charge`);
  }

  const secret = raw.clientSecret;

  if (secret !== undefined && secret !== null && typeof secret !== 'string') {
    throw new WalletContractError(`${where}: malformed charge (clientSecret)`);
  }

  const charge: tThresholdRetryCharge = {
    status: raw.status,
    amount: raw.amount,
    clientSecret:
      raw.status === 'requires_action' &&
      typeof secret === 'string' &&
      secret !== ''
        ? secret
        : null,
  };

  return { state: parseThresholdState(data.state, where), charge };
}

/**
 * Lee el `thresholdTopUp` de la respuesta de la recarga única (RET-2422): null,
 * o `{resumed}`. Ausente o con otra forma queda en null: lanzar aquí pintaría
 * un error DESPUÉS de un cobro que sí ocurrió, y el único efecto de perder
 * este campo es no afirmar que se reanudó la pausa.
 */
function parseResumption(value: unknown): { resumed: boolean } | null {
  return isRecord(value) && typeof value.resumed === 'boolean'
    ? { resumed: value.resumed }
    : null;
}

/**
 * Llave de idempotencia del PUT de la recarga por saldo bajo (KTD6):
 * `threshold:{ws}:{threshold}:{amount}:{attemptId}`.
 *
 * Incluye los VALORES, y no solo la intención, porque wallet rechaza con 400
 * una llave reusada con otros valores: cambiar el límite o el monto dentro de
 * la misma intención tiene que dar otra llave. Los mismos valores con la misma
 * intención dan la misma llave, que es lo que hace que un reenvío después de
 * un 424 no cobre dos veces.
 *
 * Cada parte pasa por `encodeURIComponent`, igual que `idempotencyKeyFor`, para
 * que ninguna pueda inyectar el separador `:`. Falla ruidoso con una parte
 * vacía, por la misma razón que `idempotencyKeyFor`: una llave incompleta pasa
 * el `required` del BFF y colisiona con otra.
 */
export function thresholdIdempotencyKeyFor(parts: {
  workspaceId: string;
  threshold: string;
  amount: string;
  attemptId: string;
}): string {
  const { workspaceId, threshold, amount, attemptId } = parts;

  if (!workspaceId || !threshold || !amount || !attemptId) {
    throw new WalletContractError(
      'wallet threshold top-up: idempotency key needs workspace, values and attempt',
    );
  }

  return [
    'threshold',
    encodeURIComponent(workspaceId),
    encodeURIComponent(threshold),
    encodeURIComponent(amount),
    encodeURIComponent(attemptId),
  ].join(':');
}

/**
 * Lo común del `catch` de las escrituras que pueden cobrar (PUT y retry):
 * 402 a `WalletDeclinedError`, 400 y 409 a `WalletRejectedError`, y todo lo
 * demás se relanza tal cual para que quien llama lo trate como desenlace
 * desconocido (424, 5xx, red) o como lo que sea (403).
 *
 * Esas llamadas van con `skipSentryCapture`, así que aquí se reporta lo que
 * ops necesita ver: todo lo que no es un rechazo del banco ni un rechazo de
 * negocio con su `reason` o su `code`. Un 409 sin `reason` (varios clientes de
 * pago, moneda) o un 400 sin `code` (llave reusada, cuerpo inválido) SÍ se
 * reportan: son contratos o datos rotos, no respuestas del flujo.
 */
function thresholdWriteError(
  error: unknown,
  context: { endpoint: string; workspaceId: string },
): unknown {
  const decline = declineFrom(error);

  if (decline) return new WalletDeclinedError(decline);

  const rejection = rejectionFrom(error);

  if (!rejection || (rejection.reason === null && rejection.code === null)) {
    sentry.captureException(error, { extra: context });
  }

  return rejection ?? error;
}

export const WalletService = Object.freeze({
  /**
   * Top-up presets and recurring info (RET-1757). Backend-owned because the
   * preset ladder scales with the plan cadence; a constant here would be wrong
   * for every customer not on a monthly plan.
   */
  getTopUpOptions: async function (
    workspaceId: string,
  ): Promise<tTopUpOptionsPayload> {
    const response = await AxiosService.get<tTopUpOptionsResponse>(
      `workspace/${workspaceId}/wallet/top-up-options`,
      { isBFFService: true, skipAutoRedirect: true },
    );

    if (!response?.success || !response.data) {
      throw new WalletContractError('wallet top-up options: malformed payload');
    }

    return response.data;
  },

  /**
   * Charges a one-off top-up (RET-1690).
   *
   * `request.paymentMethodId` is optional (RET-2428, RC-16): the card to
   * charge instead of the one wallet-service picks. It travels only when
   * present, and it is NOT part of the idempotency key: the caller pins the
   * card to the attempt (`readOrCreateOneOffAttempt`), so the same attempt
   * always sends the same card under the same key.
   *
   * `attemptId` identifies the customer's INTENT to charge, not the request:
   * the caller (TopUpModal) creates it once per intention, reuses it across
   * every retry of that same intention, and regenerates it after a successful
   * charge or when a new intention starts. The idempotency key sent to
   * wallet-service is a deterministic function of (workspaceId, amountMicros,
   * attemptId) — see `idempotencyKeyFor` above for why all three matter and
   * what breaks if any one of them is dropped.
   *
   * This closes RET-1852: if Stripe charges but the response is lost on the
   * way back, the modal stays open, the customer retries with the SAME
   * attemptId and the SAME amount, so the key repeats and Stripe dedupes
   * instead of charging twice — on a balance the SSOT (P16 / RET-1727) will
   * not refund. It also keeps a changed amount 400-free within the same
   * attempt (RET-1846) and keeps two deliberate same-amount charges on the
   * same day from silently deduping into a single, uncredited charge (Stripe
   * keys live 24h) — that second case is why the key cannot be derived from
   * (workspace, amount) alone.
   *
   * What stops a double CLICK is a different, unrelated mechanism: the
   * `isSubmitting` flag on the footer button (TopUpModal.component.tsx). That
   * flag disables the button for the lifetime of one in-flight request; it has
   * nothing to say about a retry the customer makes deliberately after the
   * button re-enables, which is exactly the case above that `attemptId` and
   * the idempotency key exist to cover.
   *
   * A 200 means CHARGED, not credited: Stripe's webhook credits the balance a
   * moment later. Callers must not repaint the balance from this response.
   *
   * A 402 is not a failure of the system, it is an answer about the card, so it
   * comes back as a typed WalletDeclinedError carrying the structured reason.
   * Sentry capture is skipped for exactly that reason: a declined card is an
   * expected business outcome, and capturing every one buries the real errors.
   *
   * That skip is per REQUEST, though, not per status: `skipSentryCapture` turns
   * Sentry off for everything this call can fail with — a 500, a 424, a dropped
   * connection, and the WalletContractError below for a malformed 200. On a money
   * path those are precisely the ones ops needs to see, so the catch re-captures
   * anything that is NOT a decline before re-throwing. The front is also the only
   * layer that observes the lost-response and malformed-200 cases at all, so
   * leaning on the BFF's own capture would not cover them.
   *
   * `amountMicros` is the CALLER's format — the front's internal one, same as
   * everywhere else in the wallet (presets and the custom-amount input both
   * live in micros; see `Wallet.money.ts` and TopUpModal's preset matching).
   * The WIRE format is different: wallet-service's `domain.Parse` expects a
   * decimal string with 6 places ("500.000000"), not micros. Sending micros
   * verbatim is the exact bug this converts around — the BFF and
   * wallet-service proxy/parse `amount` as-is, so a mismatch here 400s every
   * top-up. The conversion is `microsToDecimal` (Wallet.money.ts), never done
   * inline: business logic lives in the money module, not in a service call.
   */
  chargeTopUp: async function (
    workspaceId: string,
    request: {
      amountMicros: string;
      attemptId: string;
      paymentMethodId?: string;
    },
  ): Promise<tTopUpCharge> {
    const { amountMicros, attemptId, paymentMethodId } = request;

    try {
      const response = await AxiosService.post<
        { amount: string; idempotencyKey: string; paymentMethodId?: string },
        tTopUpChargeResponse
      >(
        `workspace/${workspaceId}/wallet/topup`,
        {
          amount: microsToDecimal(amountMicros),
          idempotencyKey: idempotencyKeyFor({
            workspaceId,
            amountMicros,
            attemptId,
          }),
          // RET-2428 (RC-16): la tarjeta indicada viaja SOLO si llega, como en
          // `retryThresholdTopUp`. Sin ella, wallet-service cobra la de RC-16.
          ...(paymentMethodId ? { paymentMethodId } : {}),
        },
        { isBFFService: true, skipAutoRedirect: true, skipSentryCapture: true },
      );

      if (!response?.success || !response.data?.providerRef) {
        // No providerRef means nothing to reconcile against Stripe, so it is not
        // a charge this app is willing to call successful.
        //
        // Nótese la asimetría, que es deliberada: se valida `providerRef`, que
        // ningún consumidor de `src` lee, y NO se valida `status`, que desde
        // RET-1870 decide todo el camino de acreditación en
        // `settleBalanceAfterCharge`. No es un descuido. Lanzar por un `status`
        // ausente pintaría un error en pantalla DESPUÉS de un cobro que sí
        // ocurrió, que es peor que el modo de falla actual: un status
        // irreconocible degrada a una sola relectura y se reporta a Sentry.
        // `providerRef` puede lanzar porque su ausencia sí significa que no hubo
        // nada que cobrar.
        throw new WalletContractError(
          'wallet top-up: malformed charge payload',
        );
      }

      // `thresholdTopUp` se lee aparte: ausente (back anterior a RET-2422) es
      // null, no un contrato roto.
      return {
        ...response.data,
        thresholdTopUp: parseResumption(response.data.thresholdTopUp),
      };
    } catch (error) {
      const decline = declineFrom(error);

      if (decline) throw new WalletDeclinedError(decline);

      // Not a decline: a real failure on the charge seam. No card data goes in —
      // the front never holds any — just what identifies the attempt.
      sentry.captureException(error, {
        extra: {
          endpoint: `workspace/${workspaceId}/wallet/topup`,
          workspaceId,
          amountMicros,
          attemptId,
        },
      });

      throw error;
    }
  },

  /**
   * Estado real de la recarga automática (RET-1762).
   *
   * Se pide aparte de `getTopUpOptions` porque la tarjeta de saldo lo necesita
   * al cargar la página, no al abrir el modal. Pedirlo del modal dejaría el
   * switch mintiendo hasta que alguien lo abra.
   */
  getRecurringTopUp: async function (
    workspaceId: string,
  ): Promise<tWireActiveRecurring> {
    const response = await AxiosService.get<tRecurringTopUpResponse>(
      `workspace/${workspaceId}/wallet/topup/recurring`,
      { isBFFService: true, skipAutoRedirect: true },
    );

    if (!response?.success || !response.data) {
      throw new WalletContractError(
        'wallet recurring top-up: malformed payload',
      );
    }

    return response.data;
  },

  /**
   * Cotiza la recarga automática bajo una periodicidad HIPOTÉTICA (RET-1719,
   * U8; endpoint de U7).
   *
   * Es la misma ruta de presets que el BFF ya expone, con `?periodicity=`. Sin
   * ese parámetro la respuesta es la de hoy, campo por campo; con él, el menú se
   * cotiza en esa cadencia y la respuesta agrega el bloque `quote`.
   *
   * Se pide APARTE de `getTopUpOptions` y no dentro, aunque el BFF componga los
   * presets ahí, porque la pregunta es distinta: `top-up-options` describe el
   * estado VIVO del workspace y la pinta el modal de recarga, mientras esto
   * pregunta por un futuro que el cliente todavía no compró. Meterla en el
   * payload vivo obligaría a que toda pantalla que lee el estado cargue con una
   * hipótesis que no le sirve.
   *
   * La periodicidad se manda desde `tWalletPeriodicity` y no como `string`: el
   * back responde 400 ante cualquier otro valor y no tiene default silencioso,
   * así que el tipo es la primera compuerta.
   *
   * NO lleva `skipSentryCapture`. Un fallo aquí no es una respuesta de negocio
   * —como sí lo es un rechazo de tarjeta—: significa que el cliente va a ver
   * "no pudimos calcular el monto nuevo" justo antes de un cambio que le mueve
   * un cobro recurrente. Eso es precisamente lo que ops necesita ver.
   */
  getRecurringTopUpQuote: async function (
    workspaceId: string,
    periodicity: tWalletPeriodicity,
  ): Promise<tWireRecurring> {
    const response = await AxiosService.get<tRecurringPresetsResponse>(
      `workspace/${workspaceId}/wallet/topup/recurring/presets?periodicity=${encodeURIComponent(periodicity)}`,
      { isBFFService: true, skipAutoRedirect: true },
    );

    if (!response?.success || !response.data) {
      throw new WalletContractError(
        'wallet recurring top-up quote: malformed payload',
      );
    }

    return response.data;
  },

  /**
   * Descarta el aviso de saldo bajo del episodio en curso (RET-1695).
   *
   * Es un POST y no un PATCH del saldo porque NO cambia el dinero: apaga un
   * aviso de interfaz, y el saldo sigue siendo exactamente el mismo después.
   *
   * El back devuelve el veredicto resultante para que el banner se repinte con
   * la respuesta, sin una segunda consulta que podría contestar sobre un saldo
   * distinto al que el cliente acaba de ver.
   */
  dismissLowBalance: async function (
    workspaceId: string,
  ): Promise<tWalletLowBalance> {
    const response = await AxiosService.post<
      Record<string, never>,
      tLowBalanceDismissResponse
    >(
      `workspace/${workspaceId}/wallet/low-balance/dismiss`,
      {},
      { isBFFService: true, skipAutoRedirect: true },
    );

    if (!response?.success || !response.data?.lowBalance) {
      throw new WalletContractError(
        'wallet low-balance dismiss: malformed payload',
      );
    }

    return response.data.lowBalance;
  },

  /**
   * Da de alta la recarga automática (RET-1692).
   *
   * El monto viaja en DECIMAL, no en micros — verificado contra
   * `RecurringTopUpService.Enable(ctx, workspaceID, amountDecimal)`, que además
   * exige que coincida EXACTO con uno de sus presets (`ErrAmountNotAPreset`).
   * Por eso se manda el `amount` tal como vino del back, sin recomponerlo desde
   * los micros: reconstruirlo es como un `50.0` se convierte en un monto que no
   * casa con `50.000000`.
   *
   * Si YA hay una activa, esta llamada SIEMPRE responde 409
   * (`ErrRecurringTopUpAlreadyActive`, GUARD 2 de wallet-service) — no la
   * reemplaza. Antes de RET-1935 un comentario en este mismo lugar afirmaba lo
   * contrario ("esta llamada la reemplaza"); esa era la creencia falsa que
   * volvía el reemplazo imposible por esta puerta (RET-1935). Para reemplazar
   * el monto de una recarga ya activa, usar `replaceRecurringTopUp` abajo.
   *
   * El aviso y la confirmación previos son responsabilidad de la UI — aquí ya
   * no hay vuelta atrás.
   */
  enableRecurringTopUp: async function (
    workspaceId: string,
    amountDecimal: string,
    options?: { replaceThreshold?: boolean },
  ): Promise<tWireActiveRecurring> {
    // RET-2425: `replaceThreshold` apaga la recarga por saldo bajo en la misma
    // operación (RC-22). Viaja SOLO en true: sin él, el cuerpo es el de
    // siempre, y con la de saldo bajo activa wallet contesta 409
    // `threshold_active`, que la UI resuelve con la confirmación del reemplazo.
    // `replaceRecurringTopUp` no lo manda: wallet lo ignora en el PUT de la
    // frecuente (`recurring_topup_controller.go:32`).
    const body: { amount: string; replaceThreshold?: true } = {
      amount: amountDecimal,
      ...(options?.replaceThreshold === true ? { replaceThreshold: true } : {}),
    };
    const response = await AxiosService.post<
      { amount: string; replaceThreshold?: true },
      tRecurringTopUpResponse
    >(`workspace/${workspaceId}/wallet/topup/recurring`, body, {
      isBFFService: true,
      skipAutoRedirect: true,
    });

    if (!response?.success || !response.data) {
      throw new WalletContractError(
        'wallet recurring top-up: enable was not confirmed',
      );
    }

    return response.data;
  },

  /**
   * Reemplaza el MONTO de la recarga automática vigente (RET-1935).
   *
   * "Reemplazar una recarga automática es imposible: el front lo ofrece y el
   * back no tiene esa operación" — ésta es la mitad del front de ese arreglo.
   * Antes de este ticket, la confirmación de reemplazo llamaba a
   * `enableRecurringTopUp`, que con una recarga ya activa SIEMPRE responde 409
   * (`ErrRecurringTopUpAlreadyActive`, GUARD 2 de wallet-service): el reemplazo
   * era, literalmente, imposible por esa puerta.
   *
   * Es un PUT, no un DELETE seguido de un POST. Encadenar los dos desde aquí
   * reabriría exactamente el hueco que el ticket prohíbe: si el POST falla
   * después de que el DELETE ya tuvo éxito, el cliente queda SIN recarga
   * automática, y sin ninguna forma de saberlo hasta el próximo ciclo sin
   * cobro. wallet-service ya hace el intercambio atómico (una sola escritura
   * en Stripe) detrás de esta única llamada.
   *
   * El monto viaja en DECIMAL tal cual el preset, igual que
   * `enableRecurringTopUp` y por el mismo motivo: wallet-service exige que
   * coincida EXACTO con uno de sus presets, así que recomponerlo desde los
   * micros es cómo un `50.0` deja de casar con `50.000000`.
   *
   * `skipAutoRedirect`: un reemplazo rechazado (409 huérfano, 404 sin activa,
   * congelado) no es una sesión inválida, y el interceptor global de 401 no
   * debe desloguear a un cliente por un rechazo de negocio.
   */
  replaceRecurringTopUp: async function (
    workspaceId: string,
    amountDecimal: string,
  ): Promise<tWireActiveRecurring> {
    const response = await AxiosService.put<
      { amount: string },
      tRecurringTopUpResponse
    >(
      `workspace/${workspaceId}/wallet/topup/recurring`,
      { amount: amountDecimal },
      { isBFFService: true, skipAutoRedirect: true },
    );

    if (!response?.success || !response.data) {
      throw new WalletContractError(
        'wallet recurring top-up: replace was not confirmed',
      );
    }

    return response.data;
  },

  /**
   * Apaga la recarga automática (RET-1762, D-12).
   *
   * Deja de cobrar el paquete desde el próximo ciclo. El saldo YA acreditado no
   * se toca — apagar no es un reverso, y la UI no debe sugerir que lo sea.
   */
  disableRecurringTopUp: async function (workspaceId: string): Promise<void> {
    const response = await AxiosService.delete<{ success?: boolean }>(
      `workspace/${workspaceId}/wallet/topup/recurring`,
      { isBFFService: true, skipAutoRedirect: true },
    );

    // El sobre se valida igual que en la lectura, y no por simetría: un 200 con
    // `success: false` resolvería la promesa, la confirmación se cerraría como
    // si hubiera funcionado, y el cliente se iría creyendo que ya no le van a
    // cobrar. La re-lectura posterior mostraría la verdad, pero para entonces
    // ya cerró la pantalla.
    if (!response?.success) {
      throw new WalletContractError(
        'wallet recurring top-up: disable was not confirmed',
      );
    }
  },

  /**
   * Estado de la recarga por saldo bajo (RET-2425): `GET …/topup/threshold`.
   *
   * Solo se llama con `billing_threshold-topup` encendido (KTD3). Sin
   * configuración wallet responde 200 con `status: off`, no 404. El BFF lo
   * devuelve SUELTO en `data`.
   *
   * Desenlaces (KTD5):
   * - 200: el estado validado. La opción de saldo bajo se ofrece.
   * - 404 con uno de los dos textos de wallet: `WalletThresholdUnavailableError`.
   *   Es negocio (sin plan o sin cliente de pago), no se reporta.
   * - Cualquier otro 404: `WalletRouteMissingError`, reportado aquí.
   * - 200 deforme: `WalletContractError`, reportado aquí.
   * - Lo demás (403, 424, 5xx, red) se relanza tal cual; `AxiosService.get` ya
   *   lo reportó. Quien llama NO vuelve a reportarlo.
   */
  getThresholdTopUp: async function (
    workspaceId: string,
  ): Promise<tThresholdTopUpState> {
    const endpoint = `workspace/${workspaceId}/wallet/topup/threshold`;
    let response: unknown;

    try {
      response = await AxiosService.get<unknown>(endpoint, {
        isBFFService: true,
        skipAutoRedirect: true,
        skipSentryCaptureForStatuses: [404],
      });
    } catch (error) {
      const notFound = notFoundFrom(error, endpoint);

      if (notFound instanceof WalletRouteMissingError) {
        sentry.captureException(error, { extra: { endpoint, workspaceId } });
      }

      throw notFound ?? error;
    }

    try {
      if (!isRecord(response) || response.success !== true) {
        throw new WalletContractError(
          'wallet threshold top-up: success flag not true',
        );
      }

      return parseThresholdState(response.data, 'wallet threshold top-up');
    } catch (error) {
      sentry.captureException(error, { extra: { endpoint, workspaceId } });
      throw error;
    }
  },

  /**
   * Programa la recarga por saldo bajo o cambia sus montos (RET-2425):
   * `PUT …/topup/threshold`. PUEDE COBRAR: si el saldo disponible ya está
   * abajo del límite nuevo y no hay un cobro en curso, wallet cobra `amount`
   * antes de guardar (RC-20).
   *
   * `threshold` y `amount` viajan como TEXTO en UNIDADES ENTERAS ("10", "25"),
   * no en micros ni con seis decimales: es el `amount` que devuelve el propio
   * estado (`thresholdAmountJSON`), y wallet los lee con `domain.Parse` y
   * rechaza con 400 `not_whole_units` lo que traiga fracción. Se mandan tal
   * cual; validar el rango es de la pantalla (U5) y de wallet.
   *
   * `attemptId` es la intención (`readOrCreateThresholdAttemptId`); la llave que
   * viaja la compone `thresholdIdempotencyKeyFor`. `replaceRecurring` viaja
   * siempre, como booleano: el BFF y wallet lo leen así.
   *
   * Desenlaces:
   * - 200: `{state, charge}`. `charge` es null cuando no se cobró. Un 200
   *   siempre significa que se guardó; comparar `state` con lo pedido es del
   *   contenedor (U6).
   * - 402: `WalletDeclinedError`, con el mismo sobre que la recarga única.
   * - 400 y 409: `WalletRejectedError` (`code`/`limitMicros` o `reason`).
   * - 424, 5xx, red: se relanza tal cual. NO dice si hubo cobro: hay que releer
   *   el GET antes de mostrar un resultado, y reenviar con la misma intención.
   */
  saveThresholdTopUp: async function (
    workspaceId: string,
    request: {
      threshold: string;
      amount: string;
      attemptId: string;
      replaceRecurring?: boolean;
    },
  ): Promise<tThresholdSaveResult> {
    const endpoint = `workspace/${workspaceId}/wallet/topup/threshold`;
    const { threshold, amount, attemptId } = request;
    const body = {
      threshold,
      amount,
      idempotencyKey: thresholdIdempotencyKeyFor({
        workspaceId,
        threshold,
        amount,
        attemptId,
      }),
      replaceRecurring: request.replaceRecurring === true,
    };
    let response: unknown;

    try {
      response = await AxiosService.put<typeof body, unknown>(endpoint, body, {
        isBFFService: true,
        skipAutoRedirect: true,
        skipSentryCapture: true,
      });
    } catch (error) {
      throw thresholdWriteError(error, { endpoint, workspaceId });
    }

    try {
      return parseThresholdSaveResult(response, 'wallet threshold top-up save');
    } catch (error) {
      // Un 200 deforme después de un PUT que pudo cobrar: ops tiene que verlo.
      sentry.captureException(error, { extra: { endpoint, workspaceId } });
      throw error;
    }
  },

  /**
   * Apaga la recarga por saldo bajo (RET-2425, la usa RET-2426):
   * `DELETE …/topup/threshold`. Idempotente: apagar una apagada es 200. Un cobro
   * en curso no se cancela. Devuelve `{state}`.
   *
   * 400 y 409 se tipan como `WalletRejectedError`; lo demás se relanza tal cual
   * (`AxiosService.delete` ya lo reporta).
   */
  disableThresholdTopUp: async function (
    workspaceId: string,
  ): Promise<tThresholdDisableResult> {
    const endpoint = `workspace/${workspaceId}/wallet/topup/threshold`;
    let response: unknown;

    try {
      response = await AxiosService.delete<unknown>(endpoint, {
        isBFFService: true,
        skipAutoRedirect: true,
      });
    } catch (error) {
      throw rejectionFrom(error) ?? error;
    }

    const where = 'wallet threshold top-up disable';

    return {
      state: parseThresholdState(thresholdDataOf(response, where).state, where),
    };
  },

  /**
   * Reintenta el cobro de una recarga por saldo bajo en pausa (RET-2426, la
   * lógica es de RET-2422): `POST …/topup/threshold/retry`.
   *
   * La llave es `threshold-retry:{ws}:{attemptId}`: un reintento no lleva
   * valores nuevos. Sin `paymentMethodId` es «Reintentar el cobro»; con él es
   * «Cambiar tarjeta» y cobra esa tarjeta sin volverla predeterminada. El BFF
   * de staging rechaza el `paymentMethodId` con 400
   * `payment_method_selection_unavailable` hasta que bff-web#1133 llegue.
   *
   * Desenlaces:
   * - 200: `{state, charge}`, con `charge.status` `charged`, `pending` o
   *   `requires_action`. Con `requires_action`, `charge.clientSecret` trae el
   *   secreto para `stripe.handleNextAction`; en los demás es null. El secreto
   *   no se copia a ningún error, a `extra` de Sentry ni a un log.
   * - 402: `WalletDeclinedError` con el motivo nuevo del banco; la pausa sigue.
   * - 400 y 409: `WalletRejectedError`. El 409 trae `not_paused`, `daily_cap`,
   *   `charge_in_progress`, `not_retryable`, `frozen`, `no_wapi` o
   *   `multiple_subscriptions`.
   * - 424, 5xx, red: se relanza tal cual; NO dice si hubo cobro.
   */
  retryThresholdTopUp: async function (
    workspaceId: string,
    request: { attemptId: string; paymentMethodId?: string },
  ): Promise<tThresholdRetryResult> {
    const endpoint = `workspace/${workspaceId}/wallet/topup/threshold/retry`;

    if (!workspaceId || !request.attemptId) {
      throw new WalletContractError(
        'wallet threshold top-up retry: idempotency key needs workspace and attempt',
      );
    }

    const body: { idempotencyKey: string; paymentMethodId?: string } = {
      idempotencyKey: [
        'threshold-retry',
        encodeURIComponent(workspaceId),
        encodeURIComponent(request.attemptId),
      ].join(':'),
      ...(request.paymentMethodId
        ? { paymentMethodId: request.paymentMethodId }
        : {}),
    };
    let response: unknown;

    try {
      response = await AxiosService.post<typeof body, unknown>(endpoint, body, {
        isBFFService: true,
        skipAutoRedirect: true,
        skipSentryCapture: true,
      });
    } catch (error) {
      throw thresholdWriteError(error, { endpoint, workspaceId });
    }

    try {
      return parseThresholdRetryResult(
        response,
        'wallet threshold top-up retry',
      );
    } catch (error) {
      sentry.captureException(error, { extra: { endpoint, workspaceId } });
      throw error;
    }
  },

  /**
   * Ledger movements, paginated (RET-1756).
   *
   * Rows arrive already grouped by date and category, and already sorted by
   * the full timestamp even though only the date shows. Grouping client-side
   * would break across page boundaries: a day split over two pages would
   * render as two partial groups (G-2/G-3).
   */
  getMovements: async function (
    workspaceId: string,
    page: number,
  ): Promise<tWalletMovementsPage> {
    const response = await AxiosService.get<tWalletMovementsResponse>(
      `workspace/${workspaceId}/wallet/movements?page=${page}`,
      { isBFFService: true, skipAutoRedirect: true },
    );

    if (!response?.success || !Array.isArray(response.data?.movements)) {
      throw new WalletContractError('wallet movements: malformed payload');
    }

    return response.data;
  },

  /**
   * El detalle de mensajes gratis de un mes (RET-2077).
   *
   * Es la puerta que abre el contador del tab de saldo, y existe para el workspace
   * que NO TIENE NINGUNA FILA que clickear en su actividad: el que solo manda
   * mensajes de servicio no mueve dinero, así que su tabla está vacía.
   *
   * ⚠️ NO LANZA cuando el backend dice `available: false`. Esa no es una falla de la
   * petición: es el backend diciendo "la mitad gratis no cargó, acá va lo que sí
   * tengo". Convertirlo en excepción borraría la distinción entre "no cargó" y "no
   * mandaste nada", que es justo lo que la bandera existe para preservar.
   *
   * Sí lanza si el payload viene deforme, que es un contrato roto y no un desenlace.
   *
   * `options.lines: false` pide el modo LIGERO (RET-2233 → RET-2283): omite el
   * desglose (`rows: []`) y sólo trae `tierCounter`, que es lo único que el
   * refresco de 60 s de `WalletProvider` necesita. El modo PESADO —el que usa
   * el tab Saldo, sin `options`— sigue saliendo EXACTAMENTE como antes de este
   * ticket: la decisión de la Rebanada 1 (whatsapp-business-service) es que el
   * peor workspace proyecta ~160,000 renglones al mes, y pedirlos cada minuto
   * es justo lo que el modo ligero existe para evitar.
   */
  getFreeMonthDetail: async function (
    workspaceId: string,
    month: string,
    options?: { lines?: boolean },
  ): Promise<tFreeMonthDetail> {
    const query = new URLSearchParams({ month });
    if (options?.lines === false) query.set('lines', 'false');

    const response = await AxiosService.get<tFreeMonthDetailResponse>(
      `workspace/${workspaceId}/wallet/consumption/free?${query.toString()}`,
      { isBFFService: true, skipAutoRedirect: true },
    );

    if (!response?.success || !Array.isArray(response.data?.rows)) {
      throw new WalletContractError(
        'wallet free month detail: malformed payload',
      );
    }

    reportUnexplainedTierCounter({
      counter: response.data.tierCounter,
      freeHalfLoaded: response.data.available,
      endpoint: 'workspace/{wid}/wallet/consumption/free',
      workspaceId,
      period: month,
    });

    return response.data;
  },

  /**
   * El detalle de UN día (RET-2231): qué costó y qué no, por país, para el día que
   * abrió la fila «Consumo de plantillas» de la actividad.
   *
   * `day` viaja como `YYYY-MM-DD`, sin zona horaria: el BFF la pone desde el header
   * de borde (CloudFront) que ya trae la del viewer, y este front nunca la manda —
   * mandarla duplicaría una decisión que ya no es suya.
   *
   * Mismo criterio que su hermano del mes (`getFreeMonthDetail`): NO LANZA cuando
   * el backend dice `freeDetailAvailable: false`. Esa no es una falla de la
   * petición: es el backend diciendo "la mitad gratis no cargó, acá va lo que sí
   * tengo" (wallet-service sigue adelante con lo cobrado cuando WBS no responde).
   * Convertirlo en excepción borraría la distinción entre "no cargó" y "no
   * mandaste nada", que es justo lo que la bandera existe para preservar.
   *
   * Sí lanza si el payload viene deforme, que es un contrato roto y no un
   * desenlace.
   */
  getDayDetail: async function (
    workspaceId: string,
    day: string,
  ): Promise<tDayDetail> {
    const response = await AxiosService.get<tDayDetailResponse>(
      `workspace/${workspaceId}/wallet/consumption?day=${day}`,
      { isBFFService: true, skipAutoRedirect: true },
    );

    if (!response?.success || !Array.isArray(response.data?.rows)) {
      throw new WalletContractError('wallet day detail: malformed payload');
    }

    // Un `amountMicros` ausente, vacío o ilegible es CONTRATO ROTO, y tiene que
    // doler aquí y no tres pantallas más abajo.
    //
    // No es hipotético. Este endpoint sirvió `amountMicros: ""` en TODOS sus
    // renglones desde que existe, porque wallet-service lo serializaba como
    // `amount_micros` y el BFF lo decodifica en su struct camelCase (RET-2235).
    // Como `BigInt("")` devuelve `0n` sin lanzar, la suma daba cero y la pantalla
    // acusaba un descuadre contra la fila de actividad: el cable roto se
    // disfrazaba de «los números no cuadran» y mandaba a investigar el dinero.
    //
    // Fallar acá convierte ese disfraz en lo que era: una petición que no cumple
    // su contrato, con su propio error y su propia entrada en Sentry.
    if (
      !response.data.rows.every((row) => isReadableMicros(row.amountMicros))
    ) {
      throw new WalletContractError(
        'wallet day detail: a row carries an unreadable amountMicros',
      );
    }

    // Una categoría que llegó y NO sabemos nombrar se reporta (RET-2241).
    //
    // `categoryLabel` la pinta como «Tipo sin identificar», igual que un asiento
    // viejo que no trae ninguna — y ahí se acaba: las dos se ven idénticas y nadie
    // se entera. El día que Meta estrene una sexta categoría, la columna diría
    // «sin identificar» indefinidamente, que es justo el estado que este ticket
    // existe para terminar.
    //
    // Se distingue del caso silencioso a propósito: sin categoría es un asiento
    // anterior al motor y es esperado; CON categoría y sin copy es un contrato que
    // se movió río arriba. Es el mismo criterio del contador del cupo, unas líneas
    // más abajo.
    const unknownCategories = [
      ...new Set(
        response.data.rows
          .map((row) => row.category)
          .filter((category): category is string => Boolean(category))
          .filter(
            (category) =>
              !(WALLET_MESSAGE_CATEGORIES as readonly string[]).includes(
                category,
              ),
          ),
      ),
    ];

    if (unknownCategories.length > 0) {
      sentry.captureException(
        new WalletContractError('wallet day detail: unknown Meta category'),
        { extra: { workspaceId, day, categories: unknownCategories } },
      );
    }

    // La misma vigilancia que la puerta del mes (RET-2236). Las dos leen el mismo
    // contador, del mismo servicio, por el mismo cable: si el campo se vuelve a
    // perder, las dos tienen que enterarse — poner el guardia sólo en una deja la
    // otra ciega a un defecto idéntico.
    reportUnexplainedTierCounter({
      counter: response.data.tierCounter,
      freeHalfLoaded: response.data.freeDetailAvailable,
      endpoint: 'workspace/{wid}/wallet/consumption',
      workspaceId,
      period: day,
    });

    return response.data;
  },

  /**
   * Cotiza el costo de enviar una plantilla ANTES de enviarla (RET-1806,
   * DS-03 §5 / DS-12 / UC-09).
   *
   * Firma EXACTA congelada por la vara del ticket: `(workspaceId, { category,
   * recipients })`. `category` es la categoría del catálogo de Meta, en
   * MINÚSCULAS (marketing | utility | authentication |
   * authentication_international | service) — nunca el template id ni el
   * `template_type` crudo del front. `recipients` son los handles a cotizar.
   *
   * Errores se registran en Sentry y se re-lanzan: quien llama decide qué
   * hacer con una cotización caída (este ticket la trata como "no se pudo
   * cotizar" y deja pasar el envío — el gate real es de backend, RET-1707).
   * No hay `skipSentryCapture` aquí: a diferencia de un 402 de cobro, una
   * cotización caída no es un desenlace de negocio esperado.
   */
  estimateSendCost: async function (
    workspaceId: string,
    params: { category: string; recipients: string[] },
  ): Promise<tSendCostEstimate> {
    try {
      const response = await AxiosService.post<
        { category: string; recipients: string[] },
        tSendCostEstimateResponse
      >(`workspace/${workspaceId}/wallet/estimate`, params, {
        isBFFService: true,
        skipAutoRedirect: true,
      });

      if (
        !response?.success ||
        !response.data ||
        !Array.isArray(response.data.unresolvedRecipients)
      ) {
        throw new WalletContractError(
          'wallet send cost estimate: malformed payload',
        );
      }

      /*
        Los micros se validan AQUÍ, en la frontera, y no donde se usan.

        Quien consume esto hace `BigInt(amountMicros)` para decidir si el saldo
        alcanza. `BigInt` lanza un SyntaxError crudo ante cualquier cosa que no
        sea un entero, y eso ocurre dentro del `try` del gate, cuyo `catch`
        existe para otra cosa —una cotización caída no bloquea el envío— así que
        se lo traga: el envío pasa, que es la dirección segura, pero el contrato
        roto desaparece sin dejar señal.

        Fallar abierto y dejar rastro son cosas distintas. El servicio existe
        para lanzar `WalletContractError` cuando el payload no es lo que dice
        ser, y ahí sí lo reporta a Sentry el `catch` de abajo.

        El signo se acepta a propósito: `balanceAfter` es NEGATIVO justo en el
        caso que motiva toda la feature. Un `/^\d+$/` aquí dejaría sin cotizar
        exactamente al cliente sin saldo.
      */
      const INTEGER_MICROS = /^-?\d+$/;
      const amounts = [
        response.data.total,
        response.data.balance,
        response.data.balanceAfter,
      ];

      if (
        amounts.some(
          (amount) => !INTEGER_MICROS.test(amount?.amountMicros ?? ''),
        )
      ) {
        throw new WalletContractError(
          'wallet send cost estimate: non-integer amountMicros',
        );
      }

      return response.data;
    } catch (error) {
      /*
        SIN `recipients`, y la omisión es deliberada: son teléfonos de clientes
        finales, y el `extra` de Sentry es el único salto de este flujo que sale
        de nuestra infraestructura — se indexa y se conserva, así que publicarlos
        ahí no se deshace borrando el evento.

        El conteo sí viaja porque es lo que de verdad se necesita para
        diagnosticar (cuántos se pidieron cotizar), y no identifica a nadie. El
        endpoint y el workspace también: sin ellos el reporte no sirve.
      */
      sentry.captureException(error, {
        extra: {
          endpoint: `workspace/${workspaceId}/wallet/estimate`,
          workspaceId,
          category: params.category,
          recipientsCount: params.recipients.length,
        },
      });

      throw error;
    }
  },

  getBalance: async function (workspaceId: string): Promise<tWalletBalance> {
    const response = await AxiosService.get<tWalletBalanceResponse>(
      `workspace/${workspaceId}/wallet/balance`,
      {
        isBFFService: true,
        // Review finding #6: the shared 401 interceptor wipes idToken /
        // currentUser / currentWorkspace from localStorage and hard-redirects
        // to '/'. A balance read must never be able to log someone out — this
        // is a passive display fetch, not an auth boundary. Especially while
        // the endpoint does not exist and its status code is unknown.
        skipAutoRedirect: true,
      },
    );

    return parseWalletBalance(response);
  },

  /**
   * El desglose del corte de créditos a saldo (RET-2142).
   *
   * `skipAutoRedirect` por el mismo motivo que el saldo: esto es una lectura
   * pasiva de presentación, y el interceptor de 401 compartido borra el
   * idToken y redirige. Un recibo histórico no puede desloguear a nadie.
   */
  getCutoverReceipt: async function (
    workspaceId: string,
  ): Promise<tWalletCutoverReceipt> {
    const response = await AxiosService.get<tWalletCutoverReceiptResponse>(
      `workspace/${workspaceId}/wallet/cutover`,
      { isBFFService: true, skipAutoRedirect: true },
    );

    // AxiosService.get ya devuelve el sobre desenvuelto, igual que en getBalance.
    return response.data;
  },
});
